/**
 * Puzzle 2 — Crow vs. Squirrel.
 *
 * Canon: "two factions that cannot intermingle". The crow's nest hangs
 * high on its tree over open ground at the east end of section 2, and a
 * thieving squirrel scurries about the foot of that tree.
 *
 * Both cats are needed (design principle #1):
 *   - Minna calms the squirrel with Calming Resonance (hold F in range
 *     until its calm bar fills). Until then it knocks any food that
 *     reaches the nest back down to the foot of the tree.
 *   - Basil lifts food into the nest with Overclocked Leap (4). The nest
 *     column starts 7u up with no ledge near it, above anything a cat
 *     reaches with an ordinary jump (Minna's best, off p3_1's west edge,
 *     puts carried food ~5.7u up), so only the leap gets it there.
 * Either cat can pick food up (Minna E, Basil ,) and carry it to the tree;
 * only Basil can take it up. Food rides 1.2u above its carrier.
 *
 * When enough food is delivered the crow hops, drops a "gear" item, and
 * flies away. `CROW_DROPPED_ITEM` fires the win condition.
 *
 * Canon (book p.13): "...sometimes they take what they are offered and
 * sometimes they demand the higher-value walnut instead." To ship that
 * beat, each puzzle setup picks a stochastic crow preference
 * (`mash` | `seeds` | `walnut`). Delivering the preferred kind double-
 * counts toward the quota; delivering a non-preferred kind still counts
 * as 1. The preference is surfaced through Aura Sight so the randomness
 * is discoverable, not invisible.
 *
 * Re-randomization: picked fresh every `setup()` call (the user spec
 * explicitly requested replayability over save-persistence), with the
 * internal `rng` hook overridable for deterministic tests.
 *
 * This puzzle owns both NPCs (Crow, Squirrel) and all its FoodItems.
 */
import { Vector3 } from "@babylonjs/core/Maths/math.vector";
import { Color3 } from "@babylonjs/core/Maths/math.color";
import { MeshBuilder } from "@babylonjs/core/Meshes/meshBuilder";
import type { Mesh } from "@babylonjs/core/Meshes/mesh";
import { StandardMaterial } from "@babylonjs/core/Materials/standardMaterial";

import { FoodItem, FoodKind } from "../../../entities/interactives/FoodItem";
import { NestPlatform } from "../../../entities/interactives/NestPlatform";
import { Crow } from "../../../entities/npcs/Crow";
import { Squirrel } from "../../../entities/npcs/Squirrel";

import { GameEvents } from "../../../config/events";
import { SCRAPYARD } from "../../../config/GameConfig";
import { tagAs } from "../../PlaceholderTag";
import type { CueDef } from "../../../ui/tutorialCues";

import type { PuzzleContext, PuzzleCue, PuzzleModule, PuzzleState } from "../../PuzzleModule";
import type { PuzzleQAHints } from "../../qa/PuzzleQAHints";

/** Canonical crow-preference pool. Ordered so [0] is the base item. */
const CROW_PREFERENCE_POOL: readonly FoodKind[] = ["mash", "seeds", "walnut"];

/**
 * The crow's nest: on its tree over the open ground between p2_5 (east
 * edge 58.75, top 6.2) and p3_1 (west edge 73, top 2.2). Nothing a cat can
 * stand on is within reach of the delivery column (see the zone below).
 */
export const CROW_NEST_POSITION = { x: 66.8, y: 8.2 } as const;

/**
 * Delivery column around the nest: carried food that rises into it is
 * delivered (once the squirrel is calm).
 *
 * The bottom (nest.y - 1.2 = 7.0u) is out of ordinary-jump reach while
 * carrying: food rides 1.2u above the carrier, so from the ground Minna
 * gets it to ~4.7u and Basil ~4.4u; her best from a ledge is ~5.7u (a
 * running jump off p3_1's west edge, coyote time included, arriving at
 * the column's east edge) and off p2_5's east edge ~4.7u. Basil's
 * Overclocked Leap (vy 22.5, apex ~17u) carries it straight through.
 * The 2.4u-wide column over flat open ground is the leap lane.
 */
export const CROW_NEST_DELIVERY_ZONE = {
  horizontalRadius: 1.2,
  minYOffset: -1.2,
  maxYOffset: 2.0,
} as const;

/** Ground point in the middle of the leap lane under the nest. */
export const CROW_LEAP_LANE = { x: CROW_NEST_POSITION.x, y: 0 } as const;

export function isInsideCrowNestDeliveryZone(
  foodPosition: Vector3,
  nestPosition: Vector3,
): boolean {
  const dx = foodPosition.x - nestPosition.x;
  const dz = foodPosition.z - nestPosition.z;
  const horizontalDistance = Math.sqrt(dx * dx + dz * dz);
  const dy = foodPosition.y - nestPosition.y;
  return (
    horizontalDistance <= CROW_NEST_DELIVERY_ZONE.horizontalRadius &&
    dy >= CROW_NEST_DELIVERY_ZONE.minYOffset &&
    dy <= CROW_NEST_DELIVERY_ZONE.maxYOffset
  );
}

/**
 * Food spawns. All three settle on the ground (food_1 under p2_3, food_2
 * under p2_4) so either cat can pick any of them up.
 */
const FOOD_SPAWNS: ReadonlyArray<{ key: string; x: number; y: number; kind: FoodKind }> = [
  { key: "food_0", x: 40, y: 1, kind: "mash" },
  { key: "food_1", x: 46, y: 1, kind: "seeds" },
  { key: "food_2", x: 52, y: 1, kind: "walnut" },
];

/** Where the squirrel starts: the foot of the crow's tree. */
const SQUIRREL_SPAWN = { x: 63, y: 0.5 } as const;

// ── HUD cue (LevelCueDriver polls getCue) ────────────────────────────

export type CrowSquirrelStep = "calm" | "fetch" | "leap" | "fed" | "solved";

export interface CrowSquirrelStepFlags {
  solved: boolean;
  crowFed: boolean;
  squirrelCalm: boolean;
  /** Basil is carrying a food item. */
  basilCarrying: boolean;
}

/**
 * Which step the HUD should point at. Calming the squirrel comes first:
 * until it is calm a delivery is knocked back down, so leaping early only
 * wastes the leap.
 */
export function crowSquirrelStep(f: CrowSquirrelStepFlags): CrowSquirrelStep {
  if (f.solved) return "solved";
  if (f.crowFed) return "fed";
  if (!f.squirrelCalm) return "calm";
  return f.basilCarrying ? "leap" : "fetch";
}

/** Cue chip per step (the same object while the step is unchanged). */
export const CROW_SQUIRREL_CUES: Record<CrowSquirrelStep, CueDef | null> = {
  calm: { cat: "minna", parts: [{ action: "purr", hold: true }, { text: "calm the squirrel" }] },
  fetch: { cat: "basil", parts: [{ action: "interact" }, { text: "pick up food" }] },
  leap: { cat: "basil", parts: [{ text: "under the nest," }, { action: "leap" }, { text: "leap" }] },
  fed: null,
  solved: null,
};

/** Objective line per step (a few words). */
export const CROW_SQUIRREL_OBJECTIVES: Record<CrowSquirrelStep, string> = {
  calm: "Calm the thieving squirrel",
  fetch: "Feed the crow",
  leap: "Leap the food up to the nest",
  fed: "Feed the crow",
  solved: "Feed the crow",
};

/** Cue anchors sit this far above the thing to act on. */
const CUE_LIFT = 1.2;

/**
 * Options for `CrowSquirrelPuzzle`. `rng` lets tests deterministically
 * pick the crow preference; production code leaves it unset and the
 * puzzle defaults to `Math.random`.
 */
export interface CrowSquirrelOptions {
  /** Returns a float in [0, 1). Defaults to `Math.random`. */
  rng?: () => number;
}

export class CrowSquirrelPuzzle implements PuzzleModule {
  readonly id = "crowSquirrel";
  readonly displayName = "Crow vs. Squirrel";

  private ctx!: PuzzleContext;

  private foodItems: FoodItem[] = [];
  private nestPlatform!: NestPlatform;
  private crow!: Crow;
  private squirrel!: Squirrel;
  private laneMarker: Mesh | null = null;
  private laneMarkerMat: StandardMaterial | null = null;

  /**
   * Count weighted by preference: delivering the preferred kind adds 2,
   * any other kind adds 1. The canon quota (`FOOD_ITEMS_NEEDED`) is
   * compared against this number, not against the raw delivery count,
   * so a lucky preferred drop feeds the crow in one go.
   */
  private foodDeliveredCount = 0;
  /** Raw count of deliveries regardless of kind. Used for telemetry. */
  private rawDeliveries = 0;
  /** Food the squirrel knocked out of the nest. */
  private snatches = 0;
  private lastSnatchToastAt = -Infinity;
  private elapsed = 0;
  private squirrelCalm = false;
  private crowFed = false;
  private solved = false;

  /**
   * The crow's chosen preference for this puzzle run. Picked in
   * `setup()`; declared non-null immediately after so the puzzle layer
   * can always read it without a null check.
   */
  private crowPreference!: FoodKind;

  /** Whether Aura Sight has already revealed the preference (one-shot hint). */
  private preferenceRevealed = false;
  private prevAuraActive = false;

  private readonly rng: () => number;

  constructor(options: CrowSquirrelOptions = {}) {
    this.rng = options.rng ?? Math.random;
  }

  setup(ctx: PuzzleContext): void {
    this.ctx = ctx;
    const { scene } = ctx;

    // Pick the crow's stochastic preference for THIS puzzle run.
    // Re-randomized every setup per user spec ("re-randomize each entry
    // — replayability"). The rng is injected via constructor for tests.
    const pickIndex = Math.floor(this.rng() * CROW_PREFERENCE_POOL.length);
    this.crowPreference = CROW_PREFERENCE_POOL[
      Math.min(pickIndex, CROW_PREFERENCE_POOL.length - 1)
    ];

    // ── Food items ──
    // Spawn 3 items even though only 2 are required (FOOD_ITEMS_NEEDED):
    // one redundancy without changing the win condition. Canon p.13:
    // mash + seeds + walnut. Order matches `CROW_PREFERENCE_POOL` so an
    // indexing change is caught by `crow-squirrel-preference.spec.ts`.
    this.foodItems = FOOD_SPAWNS.map(
      (f) => new FoodItem(scene, f.key, new Vector3(f.x, f.y, 0), f.kind),
    );

    // ── Crow nest (delivery target) ──
    const zone = CROW_NEST_DELIVERY_ZONE;
    this.nestPlatform = new NestPlatform(scene, {
      key: "crowNest",
      position: new Vector3(CROW_NEST_POSITION.x, CROW_NEST_POSITION.y, 0),
      label: "Crow Nest",
      requiredCount: SCRAPYARD.PUZZLE.FOOD_ITEMS_NEEDED,
      showDeliveryZone: true,
      deliveryRadius: zone.horizontalRadius,
      deliveryHeight: zone.maxYOffset - zone.minYOffset,
      deliveryYOffset: (zone.minYOffset + zone.maxYOffset) / 2,
    });

    // Leap lane: a brass ring on the ground under the nest, so the spot
    // to stand for the leap reads from where the cats are (the nest sits
    // near the top of the frame).
    this.createLaneMarker();

    // ── Crow (idles above its nest) ──
    this.crow = new Crow(
      scene,
      "scrapCrow",
      new Vector3(CROW_NEST_POSITION.x, CROW_NEST_POSITION.y + 1, 0),
    );

    // ── Squirrel (agitated at the foot of the tree) ──
    this.squirrel = new Squirrel(
      scene,
      "scrapSquirrel",
      new Vector3(SQUIRREL_SPAWN.x, SQUIRREL_SPAWN.y, 0),
      [SCRAPYARD.NPC.SQUIRREL_PATROL_MIN, SCRAPYARD.NPC.SQUIRREL_PATROL_MAX],
    );

    // ── AuraSight interactives ──
    ctx.registerInteractive({
      mesh: this.nestPlatform.rootMesh,
      hidden: false,
      state: "revealed",
    });

    // ── Food delivered event: count (double-weighted for preferred
    //    kind), feed crow when quota met ──
    ctx.addUnsub(
      ctx.gameEvents.on(GameEvents.FOOD_DELIVERED, (payload) => {
        // Preferred kind double-counts (canon p.13 "higher-value walnut").
        // Missing `kind` (old payloads) defaults to a single count so
        // any scene dispose races during shutdown can't zero a delivery.
        const kind = payload?.kind;
        const isPreferred = kind === this.crowPreference;
        const weight = isPreferred ? 2 : 1;
        this.foodDeliveredCount += weight;
        this.rawDeliveries += 1;

        // Toast the reveal when the player sees the preferred kind land
        // even without Aura Sight — the nest visual alone won't tell
        // them why they just jumped from 1→3 on a single drop.
        if (isPreferred && !this.preferenceRevealed) {
          this.preferenceRevealed = true;
          ctx.gameEvents.emit(GameEvents.HUD_TOAST, {
            message: `The crow's favourite! (${this.crowPreference}) — counts double.`,
          });
        }

        // Fire the win condition once the *weighted* count hits the
        // canon quota. `deliverFood()` is called once per weight unit
        // so the nest's visual bird-food pile matches the math.
        for (let i = 0; i < weight; i++) {
          const reachedQuota = this.nestPlatform.deliverFood();
          if (reachedQuota && !this.crowFed) {
            this.crow.feed();
            this.crowFed = true;
            // Forensic-QA event: distinct from PUZZLE_PROGRESSED so the
            // telemetry recorder can pinpoint the moment the crow is
            // satisfied without parsing generic progress steps.
            ctx.gameEvents.emit(GameEvents.CROW_FED, {
              npcKey: this.crow.npcKey,
              foodCount: this.foodDeliveredCount,
            });
            ctx.gameEvents.emit(GameEvents.PUZZLE_PROGRESSED, {
              puzzleId: this.id,
              step: "crowFed",
            });
            break;
          }
        }
      }),
    );

    // ── Crow dropped gear item = puzzle solved ──
    ctx.addUnsub(
      ctx.gameEvents.on(GameEvents.CROW_DROPPED_ITEM, () => {
        if (this.solved) return;
        this.solved = true;
        ctx.gameEvents.emit(GameEvents.PUZZLE_SOLVED, { puzzleId: this.id });
      }),
    );
  }

  update(dt: number, elapsed: number): void {
    this.elapsed = elapsed;
    const { minna, auraSight, calmingResonance } = this.ctx;

    // Aura Sight preference reveal: the first time Minna toggles the
    // overlay on while the crow is still waiting, surface a HUD toast
    // naming the preferred kind. Rising-edge only so re-toggling doesn't
    // spam the prompt panel. Once the crow is fed the reveal is moot.
    const auraActive = auraSight.isActive;
    if (
      auraActive &&
      !this.prevAuraActive &&
      !this.preferenceRevealed &&
      !this.crowFed
    ) {
      this.preferenceRevealed = true;
      this.ctx.gameEvents.emit(GameEvents.HUD_TOAST, {
        message: `Aura Sight: the crow prefers ${this.crowPreference} (double-count).`,
      });
    }
    this.prevAuraActive = auraActive;

    // Squirrel: agitated until Minna's Purr fills its calm meter; a calm
    // squirrel stays calm. It scurries about the foot of the tree.
    if (!this.crowFed) {
      this.squirrel.updateBehavior(
        dt,
        calmingResonance.isActive,
        minna.mesh.position.x,
        minna.mesh.position.y,
      );
      if (!this.squirrelCalm && this.squirrel.behavior.state === "frantic") {
        const targetFood = this.squirrel.checkForFood(
          this.foodItems.map((f) => ({
            position: f.mesh.position,
            isActive: !f.isCarried && !f.isDelivered,
          })),
        );
        if (targetFood) this.squirrel.rushToward(targetFood.position.x, dt);
      }
      this.squirrel.mesh.position.x = Math.max(
        SCRAPYARD.NPC.SQUIRREL_PATROL_MIN,
        Math.min(SCRAPYARD.NPC.SQUIRREL_PATROL_MAX, this.squirrel.mesh.position.x),
      );
      if (!this.squirrelCalm && this.squirrel.behavior.isCalmed) {
        this.markSquirrelCalm();
      }
    }
    this.squirrel.update(elapsed, dt);

    // Food items follow carriers + check delivery. While the squirrel is
    // agitated it knocks anything that reaches the nest back down.
    for (const food of this.foodItems) {
      food.update();
      if (!food.isCarried || food.isDelivered) continue;
      if (
        !isInsideCrowNestDeliveryZone(
          food.mesh.position,
          this.nestPlatform.rootMesh.position,
        )
      ) {
        continue;
      }
      if (this.squirrelCalm) {
        food.deliver();
      } else {
        this.snatch(food);
      }
    }

    // Crow idle bob
    this.crow.update(elapsed, dt);
  }

  private markSquirrelCalm(): void {
    this.squirrelCalm = true;
    this.ctx.gameEvents.emit(GameEvents.PUZZLE_PROGRESSED, {
      puzzleId: this.id,
      step: "squirrelCalmed",
    });
    this.ctx.gameEvents.emit(GameEvents.HUD_TOAST, {
      message: "The squirrel curls up at the foot of the tree. The nest is safe.",
    });
  }

  /** The agitated squirrel knocks `food` out of the nest to the tree's foot. */
  private snatch(food: FoodItem): void {
    const s = this.squirrel.mesh.position;
    food.dropAt(new Vector3(s.x, 1.0, 0));
    this.squirrel.grabFood();
    this.snatches += 1;
    this.ctx.gameEvents.emit(GameEvents.PUZZLE_PROGRESSED, {
      puzzleId: this.id,
      step: "squirrelSnatched",
    });
    if (this.elapsed - this.lastSnatchToastAt > 4) {
      this.lastSnatchToastAt = this.elapsed;
      this.ctx.gameEvents.emit(GameEvents.HUD_TOAST, {
        message: "The squirrel knocked it out of the nest! Minna has to purr it calm first (F).",
      });
    }
  }

  private createLaneMarker(): void {
    const { scene } = this.ctx;
    const ring = MeshBuilder.CreateTorus(
      "crowNest_leapLane",
      {
        diameter: CROW_NEST_DELIVERY_ZONE.horizontalRadius * 2,
        thickness: 0.08,
        tessellation: 32,
      },
      scene,
    );
    ring.position = new Vector3(CROW_LEAP_LANE.x, CROW_LEAP_LANE.y + 0.04, 0);
    ring.isPickable = false;
    const mat = new StandardMaterial("crowNest_leapLane_mat", scene);
    mat.diffuseColor = new Color3(0.95, 0.72, 0.28);
    mat.emissiveColor = new Color3(0.5, 0.33, 0.08);
    mat.specularColor = Color3.Black();
    mat.alpha = 0.8;
    mat.disableLighting = true;
    mat.metadata = { preserveAffordanceEmissive: true };
    ring.material = mat;
    tagAs(ring, "modular-final");
    this.laneMarker = ring;
    this.laneMarkerMat = mat;
  }

  /**
   * Attempt a food pickup by a given carrier. Returns true if a nearby
   * food item was picked up. Used by the scene shell to route the Minna
   * "E" and Basil "," keybinds.
   */
  tryPickupFood(carrier: import("@babylonjs/core/Meshes/mesh").Mesh): boolean {
    for (const food of this.foodItems) {
      if (food.isCarried || food.isDelivered) continue;
      if (Vector3.Distance(carrier.position, food.mesh.position) < 2) {
        food.pickUp(carrier);
        return true;
      }
    }
    return false;
  }

  /**
   * Find any food item currently carried by the given mesh. Used by the
   * scene shell so the Minna "E" key can drop-before-toggling stealth,
   * removing the dual-role footgun where pressing E mid-delivery would
   * silently activate Shadow Slip and break the player out of carry mode.
   */
  carriedBy(carrier: import("@babylonjs/core/Meshes/mesh").Mesh): FoodItem | null {
    for (const food of this.foodItems) {
      // FoodItem doesn't expose its carrier directly; the carrier flag is
      // sufficient because each cat can only hold one item at a time and
      // the proximity check in update() uses position equality at the
      // carry offset. Match on Y delta + isCarried.
      if (!food.isCarried || food.isDelivered) continue;
      const dx = food.mesh.position.x - carrier.position.x;
      const dz = food.mesh.position.z - carrier.position.z;
      if (Math.abs(dx) < 0.1 && Math.abs(dz) < 0.1) {
        return food;
      }
    }
    return null;
  }

  getState(): PuzzleState {
    if (this.solved) return { solved: true, progress: 1 };
    // 25% squirrel calmed, 50% delivery, 20% crow fed (waiting for the
    // gear drop). Uses the weighted `foodDeliveredCount` so a double-count
    // preferred drop fills the bar twice as fast — matching the quota
    // math in the FOOD_DELIVERED handler above.
    const calm = this.squirrelCalm ? 0.25 : 0;
    const deliveryProgress =
      Math.min(1, this.foodDeliveredCount / SCRAPYARD.PUZZLE.FOOD_ITEMS_NEEDED) *
      0.5;
    const fedBonus = this.crowFed ? 0.2 : 0;
    return {
      solved: false,
      progress: Math.min(0.95, calm + deliveryProgress + fedBonus),
    };
  }

  /** The step the HUD cue points at (see `crowSquirrelStep`). */
  getStep(): CrowSquirrelStep {
    return crowSquirrelStep({
      solved: this.solved,
      crowFed: this.crowFed,
      squirrelCalm: this.squirrelCalm,
      basilCarrying: this.carriedBy(this.ctx.basil.mesh) !== null,
    });
  }

  /**
   * HUD guidance for LevelCueDriver: the squirrel while it is agitated
   * (Minna purrs), then the nearest loose food (Basil picks it up), then
   * the leap lane under the nest (Basil leaps).
   */
  getCue(): PuzzleCue | null {
    const step = this.getStep();
    if (step === "solved") return null;
    const cue = CROW_SQUIRREL_CUES[step];
    const objective = CROW_SQUIRREL_OBJECTIVES[step];
    const at = this.cueTarget(step);
    if (!cue || !at) return { objective, cue: null };
    return { objective, cue, anchor: { x: at.x, y: at.y + CUE_LIFT, z: 0 } };
  }

  private cueTarget(step: CrowSquirrelStep): { x: number; y: number } | null {
    switch (step) {
      case "calm":
        return this.squirrel.mesh.position;
      case "fetch": {
        const b = this.ctx.basil.mesh.position;
        let best: FoodItem | null = null;
        let bestD = Infinity;
        for (const food of this.foodItems) {
          if (food.isDelivered) continue;
          // Loose food first; food Minna carries only if nothing is loose.
          const d = Math.abs(food.mesh.position.x - b.x) + (food.isCarried ? 1000 : 0);
          if (d < bestD) { bestD = d; best = food; }
        }
        return best ? best.mesh.position : null;
      }
      case "leap":
        return CROW_LEAP_LANE;
      default:
        return null;
    }
  }

  // ── Public probes for the content-QA unit tests ────────────────────
  // `getCrowPreference` and `getDeliveredWeight` let the spec assert the
  // per-run pick and the double-count math without poking at private
  // state. Keep these stable — removing them breaks
  // `crow-squirrel-preference.spec.ts`.

  /** Preferred food kind for the current puzzle run. */
  getCrowPreference(): FoodKind {
    return this.crowPreference;
  }

  /** Weighted delivery count (preferred=2, other=1). */
  getDeliveredWeight(): number {
    return this.foodDeliveredCount;
  }

  /** Raw count of deliveries regardless of kind. */
  getRawDeliveries(): number {
    return this.rawDeliveries;
  }

  /** Whether the preference has been revealed to the player yet. */
  isPreferenceRevealed(): boolean {
    return this.preferenceRevealed;
  }

  /** Whether Minna's Purr has calmed the squirrel (deliveries count). */
  isSquirrelCalm(): boolean {
    return this.squirrelCalm;
  }

  /** Food the squirrel has knocked out of the nest so far. */
  getSnatchCount(): number {
    return this.snatches;
  }

  /** Live state for QA tooling and the whole-level route. */
  getInternals() {
    const s = this.squirrel.mesh.position;
    return {
      id: this.id,
      step: this.getStep(),
      squirrelCalm: this.squirrelCalm,
      squirrel: { x: s.x, y: s.y, state: this.squirrel.behavior.state },
      nest: { x: CROW_NEST_POSITION.x, y: CROW_NEST_POSITION.y },
      leapLane: { ...CROW_LEAP_LANE },
      snatches: this.snatches,
      preference: this.crowPreference,
      food: this.foodItems.map((f) => ({
        key: f.foodKey,
        kind: f.kind,
        x: f.mesh.position.x,
        y: f.mesh.position.y,
        carried: f.isCarried,
        delivered: f.isDelivered,
      })),
    };
  }

  getQAHints(): PuzzleQAHints {
    const nest = CROW_NEST_POSITION;
    const lane = CROW_LEAP_LANE;
    return {
      puzzleId: this.id,
      successEvent: GameEvents.PUZZLE_SOLVED,
      targets: [
        { id: "food_0", meshName: "food_food_0", position: { x: 40, y: 0.2 }, radius: 1.0, allowedCat: "either", label: "Food mash" },
        { id: "food_1", meshName: "food_food_1", position: { x: 46, y: 0.2 }, radius: 1.0, allowedCat: "either", label: "Food seeds" },
        { id: "food_2", meshName: "food_food_2", position: { x: 52, y: 0.2 }, radius: 1.0, allowedCat: "either", label: "Food walnut" },
        { id: "squirrel", meshName: "npc_scrapSquirrel", position: { x: SQUIRREL_SPAWN.x, y: SQUIRREL_SPAWN.y }, radius: 2.0, allowedCat: "minna", label: "Squirrel (Minna purrs it calm)" },
        { id: "nest", position: { x: nest.x, y: nest.y }, radius: CROW_NEST_DELIVERY_ZONE.horizontalRadius, allowedCat: "basil", label: "Crow nest delivery column (Overclocked Leap only)" },
        { id: "crow", meshName: "npc_scrapCrow", position: { x: nest.x, y: nest.y + 1 }, radius: 2.0, allowedCat: "either", label: "Crow perch" },
        { id: "delivery_lane", position: { x: lane.x, y: lane.y + 0.7 }, radius: CROW_NEST_DELIVERY_ZONE.horizontalRadius, allowedCat: "basil", label: "Leap lane under the nest (open ground)" },
      ],
      traversals: [
        // Basil stands in the lane (open ground, nothing overhead) and
        // taps 4: OvLeap charges 350 ms then fires straight up (vy 22.5)
        // and the carried food crosses the delivery column on the way up.
        {
          id: "basil_ovleap_to_nest",
          kind: "ovLeap",
          cat: "basil",
          from: "delivery_lane",
          to: "nest",
          successWindowMs: 5_000,
          recipe: [
            { key: "4", holdMs: 100 },
          ],
        },
      ],
    };
  }

  /**
   * Sub-puzzle state seeder. Shape:
   *   {
   *     squirrelCalm?: boolean,      // calm the squirrel (deliveries count)
   *     crowFed?: boolean,           // mark crow fed (quota hit, waiting for gear drop)
   *     foodDelivered?: number,      // set weighted delivery count
   *     solved?: boolean,            // fire CROW_DROPPED_ITEM + PUZZLE_SOLVED
   *   }
   *
   * Most valuable seed is `{ solved: true }` for traces testing the
   * post-puzzle portal/Elder sequencing. `{ squirrelCalm: true }` skips
   * Minna's part so a trace can exercise the leap deliveries alone.
   */
  seed(state: Record<string, unknown>): void {
    if (state.squirrelCalm === true || state.crowFed === true || state.solved === true) {
      this.calmSquirrelNow();
    }
    if (typeof state.foodDelivered === "number") {
      this.foodDeliveredCount = Math.max(0, state.foodDelivered);
    }
    if (state.crowFed === true && !this.crowFed) {
      this.crowFed = true;
      this.foodDeliveredCount = Math.max(
        this.foodDeliveredCount,
        SCRAPYARD.PUZZLE.FOOD_ITEMS_NEEDED,
      );
      this.crow.feed();
      this.ctx.gameEvents.emit(GameEvents.CROW_FED, {
        npcKey: this.crow.npcKey,
        foodCount: this.foodDeliveredCount,
      });
      this.ctx.gameEvents.emit(GameEvents.PUZZLE_PROGRESSED, {
        puzzleId: this.id,
        step: "crowFed",
      });
    }
    if (state.solved === true && !this.solved) {
      if (!this.crowFed) {
        this.crowFed = true;
        this.foodDeliveredCount = Math.max(
          this.foodDeliveredCount,
          SCRAPYARD.PUZZLE.FOOD_ITEMS_NEEDED,
        );
        this.crow.feed();
      }
      this.solved = true;
      this.ctx.gameEvents.emit(GameEvents.CROW_DROPPED_ITEM, {
        npcKey: this.crow.npcKey,
        x: this.crow.mesh.position.x,
        y: this.crow.mesh.position.y - 0.5,
      });
      this.ctx.gameEvents.emit(GameEvents.PUZZLE_SOLVED, { puzzleId: this.id });
    }
  }

  /** Seed path: put the squirrel straight into its calmed state. */
  private calmSquirrelNow(): void {
    if (this.squirrelCalm) return;
    const b = this.squirrel.behavior;
    b.calmMeter = b.config.calmThreshold;
    b.setState("calmed");
    this.squirrelCalm = true;
  }

  dispose(): void {
    for (const food of this.foodItems) food.dispose();
    this.nestPlatform.dispose();
    this.crow.dispose();
    this.squirrel.dispose();
    this.laneMarker?.dispose();
    this.laneMarkerMat?.dispose();
  }
}
